import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import useAuth from '../../hooks/useAuth';
import { isMaster, isUsuarioComum } from '../../utils/perfil';
import { Linha, Pizza } from './GraficosAssistente';
import { manualItems, manualScreens } from '../../sections/dashboard/novidades/novidadesData';

/* ============================================================================
   Assistente (21/10/2026) - botao flutuante com robo + painel de conversa.

   So consulta: a API responde em blocos (`POST assistente/perguntar`) e aqui
   cada bloco e renderizado como TEXTO - nunca como HTML. Perguntas de "como
   fazer" (`intencao === 'MANUAL'`, ou a API fora do ar) sao respondidas pelo
   manual LOCAL (novidadesData), sem ir ao servidor.

   O botao fica acima do de mensagem na tela de Tickets (o de mensagem so
   existe la) e chama atencao a cada ~30-45 s com um balanco curto, sem animar
   para quem pediu `prefers-reduced-motion` (variante `motion-safe`).
   ============================================================================ */

type Tom = 'ok' | 'alerta' | 'perigo' | 'neutro';
type Bloco =
  | { tipo: 'titulo'; titulo: string; subtitulo?: string }
  | { tipo: 'texto'; texto: string }
  | { tipo: 'metricas'; itens: { rotulo: string; valor: string | number; tom?: Tom }[] }
  | { tipo: 'barras'; titulo?: string; itens: { rotulo: string; valor: number }[] }
  | { tipo: 'pizza'; titulo?: string; itens: { rotulo: string; valor: number }[] }
  | { tipo: 'linha'; titulo?: string; pontos: { rotulo: string; valor: number }[] }
  | { tipo: 'tabela'; titulo?: string; colunas: string[]; linhas: (string | number | null)[][]; links?: (string | null)[] }
  | { tipo: 'passos'; passos: string[] } // so local (manual)
  | { tipo: 'link'; rotulo: string; to: string }; // so local
type Msg = { papel: 'eu' | 'bot'; texto?: string; blocos?: Bloco[]; sugestoes?: string[]; erro?: boolean; hora?: string; ajuda?: boolean };

const CHAVE = 'gpshub:assistente:v1';
// Perguntas iniciais por assunto (22/10/2026): 20 botoes soltos nao se leem.
const SUGESTOES: { assunto: string; perguntas: string[] }[] = [
  { assunto: 'Parque', perguntas: ['resumo do parque', 'aparelhos online', 'aparelhos por regional', 'versão do GPSHub nos aparelhos'] },
  { assunto: 'Atenção', perguntas: ['aparelhos perdidos', 'aparelhos sem contato há 7 dias', 'aparelhos pendentes', 'bateria baixa'] },
  { assunto: 'Novos', perguntas: ['aparelhos cadastrados hoje', 'aparelhos cadastrados por mês'] },
  { assunto: 'Grupos e apps', perguntas: ['grupos sem política', 'grupos sem aparelhos', 'quem tem o WhatsApp instalado?', 'aplicativos que mais consomem'] },
  { assunto: 'Tickets', perguntas: ['meus tickets abertos', 'tickets por mês'] },
  { assunto: 'Como fazer', perguntas: ['como envio uma rede Wi-Fi para os aparelhos?', 'como coloco um aparelho em modo perdido?'] },
];
/* Perfil Usuario comum (0) - 23/10/2026: so o que o servidor responde para ele
   (intencao.ts INTENCOES_USUARIO_COMUM). E conveniencia: o bloqueio e na API. */
const SUGESTOES_USUARIO: typeof SUGESTOES = [
  { assunto: 'Parque', perguntas: ['resumo do parque'] },
  { assunto: 'Grupos', perguntas: ['grupos sem política', 'grupos sem aparelhos', 'grupos com mais aparelhos'] },
  SUGESTOES[4], // Tickets
  // Sem Wi-Fi e modo perdido: sao telas que o menu dele nao tem.
  { assunto: 'Como fazer', perguntas: ['como faço para abrir um ticket?', 'como faço para usar o QR Code do grupo?'] },
];

/* Balao que aparece de tempo em tempo ao lado do robo fechado. So perguntas que
   todo perfil pode fazer (o perfil 0 tambem ve o balao). */
const DICAS = [
  'Precisa de ajuda? Pergunte para mim.',
  'Experimente: "resumo do parque"',
  'Digite o nome de um grupo ou aparelho.',
  'Dúvida de como fazer? Eu procuro no manual.',
];

const num = (v: string | number | null | undefined) => (typeof v === 'number' ? v.toLocaleString('pt-BR') : v ?? '');
const hhmm = () => new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const comHora = (m: Msg): Msg => ({ ...m, hora: hhmm() });
const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const PARADAS = new Set(['como', 'para', 'que', 'uma', 'uns', 'por', 'com', 'dos', 'das', 'aos', 'faco', 'posso', 'devo', 'envio', 'qual', 'onde']);
const palavras = (s: string) =>
  semAcento(s)
    .split(/[^a-z0-9]+/)
    .filter((p) => p.length > 2 && !PARADAS.has(p));

const TOM: Record<Tom, string> = {
  ok: 'border-l-ok',
  alerta: 'border-l-warn',
  perigo: 'border-l-danger',
  neutro: 'border-l-line',
};

function IconeRobo({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3v3" />
      <circle cx="12" cy="2.6" r="0.6" fill="currentColor" />
      <rect x="4" y="6" width="16" height="12" rx="3.5" />
      <circle cx="9" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <path d="M9.5 15.3h5" />
      <path d="M2 11v3M22 11v3" />
      <path d="M8 18v2h8v-2" />
    </svg>
  );
}

/* Barras: mostra 8 e expande com "ver todos". */
function Barras({ titulo, itens }: { titulo?: string; itens: { rotulo: string; valor: number }[] }) {
  const [todos, setTodos] = useState(false);
  const max = Math.max(1, ...itens.map((x) => x.valor));
  const soma = itens.reduce((s, x) => s + x.valor, 0) || 1;
  const vis = todos ? itens : itens.slice(0, 8);
  return (
    <div className="space-y-1.5">
      {titulo && <div className="text-[12px] font-semibold text-ink">{titulo}</div>}
      {!itens.length && <div className="text-[12px] text-muted-foreground">Sem dados</div>}
      {vis.map((x, j) => (
        <div key={j} className="text-[12px]">
          <div className="flex justify-between gap-2">
            <span className="truncate text-ink">{x.rotulo}</span>
            <span className="shrink-0 text-muted-foreground">
              {num(x.valor)} · {Math.round((x.valor / soma) * 100)}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-soft">
            <div className="h-1.5 rounded-full bg-primary" style={{ width: `${(x.valor / max) * 100}%` }} />
          </div>
        </div>
      ))}
      {itens.length > 8 && (
        <button type="button" onClick={() => setTodos((v) => !v)} className="text-[11.5px] font-medium text-info hover:underline">
          {todos ? 'ver menos' : `ver todos (${itens.length})`}
        </button>
      )}
    </div>
  );
}

/* Copia a tabela como texto tabulado (clipboard; fallback por selecao). */
function CopiarTabela({ colunas, linhas }: { colunas: string[]; linhas: (string | number | null)[][] }) {
  const [ok, setOk] = useState(false);
  const copiar = async () => {
    const t = [colunas, ...linhas.map((l) => l.map((c) => String(c ?? '')))].map((l) => l.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(t);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = t;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* sem clipboard */
      }
      document.body.removeChild(ta);
    }
    setOk(true);
    setTimeout(() => setOk(false), 1500);
  };
  return (
    <button type="button" onClick={copiar} className="shrink-0 rounded border border-line px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-soft">
      {ok ? 'copiado' : 'copiar'}
    </button>
  );
}

/* Melhor item do manual local para a pergunta; null se nada servir. */
function buscarNoManual(pergunta: string, master: boolean) {
  const q = palavras(pergunta);
  if (!q.length) return null;
  type Cand = { pontos: number; titulo: string; descricao: string; passos: string[]; to?: string };
  const cands: Cand[] = [];
  const pontuar = (titulo: string, resto: string) => {
    const t = new Set(palavras(titulo));
    const r = new Set(palavras(resto));
    return q.reduce((s, p) => s + (t.has(p) ? 3 : 0) + (r.has(p) ? 1 : 0), 0);
  };
  manualItems.forEach((m) =>
    cands.push({ pontos: pontuar(m.title, m.description + ' ' + m.steps.join(' ')), titulo: m.title, descricao: m.description, passos: m.steps })
  );
  manualScreens
    .filter((s) => master || !s.soMaster)
    .forEach((s) =>
      cands.push({
        pontos: pontuar(s.title, `${s.description} ${s.usage} ${s.highlights.join(' ')}`),
        titulo: `Tela ${s.title}`,
        descricao: s.description,
        passos: s.visualGuide?.steps ?? [s.usage],
        to: s.path,
      })
    );
  const ord = cands.filter((c) => c.pontos > 0).sort((a, b) => b.pontos - a.pontos);
  return ord.length ? { melhor: ord[0], outras: ord.slice(1, 3) } : null;
}

function respostaManual(pergunta: string, master: boolean): Msg {
  const r = buscarNoManual(pergunta, master);
  if (!r) {
    return {
      papel: 'bot',
      blocos: [
        { tipo: 'texto', texto: 'Não achei isso no manual. Se precisar, abra um ticket e o suporte te ajuda.' },
        { tipo: 'link', rotulo: 'Abrir Tickets', to: '/dashboard/tickets' },
      ],
    };
  }
  const { melhor, outras } = r;
  const blocos: Bloco[] = [
    { tipo: 'titulo', titulo: melhor.titulo, subtitulo: 'Como fazer' },
    { tipo: 'texto', texto: melhor.descricao },
    { tipo: 'passos', passos: melhor.passos },
  ];
  if (melhor.to) blocos.push({ tipo: 'link', rotulo: 'Ir para a tela', to: melhor.to });
  // Alternativas viram sugestoes clicaveis (o titulo do manual casa com a propria busca).
  return { papel: 'bot', blocos, sugestoes: outras.map((o) => o.titulo) };
}

function carregar(): Msg[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(CHAVE) || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

export default function Assistente() {
  const { user } = useAuth();
  const master = isMaster(user);
  const usuarioComum = isUsuarioComum(user);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const naTickets = pathname.replace(/\/+$/, '') === '/dashboard/tickets';

  const [aberto, setAberto] = useState(false);
  const [cheio, setCheio] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>(carregar);
  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  const [chamando, setChamando] = useState(false);
  const [dica, setDica] = useState(-1); // indice em DICAS; avanca a cada chamada
  const fim = useRef<HTMLDivElement>(null);
  const hist = useRef(-1); // posicao no historico de perguntas (seta para cima)

  useEffect(() => {
    try {
      sessionStorage.setItem(CHAVE, JSON.stringify(msgs.slice(-40)));
    } catch {
      /* sem storage: so memoria */
    }
    fim.current?.scrollIntoView({ block: 'end' });
  }, [msgs, pensando, aberto]);

  // Esc fecha (minimiza) o painel; tela cheia volta ao normal primeiro.
  useEffect(() => {
    if (!aberto) return undefined;
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (cheio) setCheio(false);
      else setAberto(false);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [aberto, cheio]);

  /* Chama atencao enquanto fechado: a primeira vez aos 8 s, depois a cada
     30-45 s. Cada chamada dura 5 s: balanco do robo (1,2 s), anel pulsando e um
     balao com uma dica (23/10/2026, a pedido - so o balanco passava batido, e
     some de vez com "efeitos de animacao" desligados no Windows; o balao nao
     depende de animacao). */
  useEffect(() => {
    if (aberto) return undefined;
    let t1: ReturnType<typeof setTimeout>;
    let t2: ReturnType<typeof setTimeout>;
    const agendar = (espera: number) => {
      t1 = setTimeout(() => {
        setDica((d) => (d + 1) % DICAS.length);
        setChamando(true);
        t2 = setTimeout(() => setChamando(false), 5000);
        agendar(30000 + Math.random() * 15000);
      }, espera);
    };
    agendar(8000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      setChamando(false);
    };
  }, [aberto]);

  const perguntar = useCallback(
    async (pergunta: string) => {
      const p = pergunta.trim();
      if (!p || pensando) return;
      hist.current = -1;
      setMsgs((m) => [...m, { papel: 'eu', texto: p, hora: hhmm() }]);
      setTexto('');
      setPensando(true);
      try {
        const res = await axiosInstance.post<{ intencao: string; blocos: Bloco[]; sugestoes?: string[] }>('assistente/perguntar', { pergunta: p });
        const d = res.data;
        if (d?.intencao === 'MANUAL') setMsgs((m) => [...m, comHora(respostaManual(p, master))]);
        // AJUDA: o texto da API + as perguntas por assunto (clicaveis), sem as sugestoes soltas.
        else if (d?.intencao === 'AJUDA') setMsgs((m) => [...m, { papel: 'bot', blocos: d.blocos, ajuda: true, hora: hhmm() }]);
        else if (Array.isArray(d?.blocos) && d.blocos.length) setMsgs((m) => [...m, { papel: 'bot', blocos: d.blocos, sugestoes: d.sugestoes, hora: hhmm() }]);
        else setMsgs((m) => [...m, { papel: 'bot', texto: 'Não encontrei nada para essa pergunta. Tente reformular.', hora: hhmm() }]);
      } catch (e: any) {
        if (semAcento(p).startsWith('como')) setMsgs((m) => [...m, comHora(respostaManual(p, master))]);
        else
          setMsgs((m) => [
            ...m,
            { papel: 'bot', erro: true, hora: hhmm(), texto: e?.response?.data?.message || 'Não consegui consultar agora. Verifique a conexão e tente de novo.' },
          ]);
      } finally {
        setPensando(false);
      }
    },
    [pensando, master]
  );

  const ir = (to: string) => {
    navigate(to);
    if (window.innerWidth < 640) setAberto(false);
  };

  const sugestao = (s: string) => (
    <button
      key={s}
      type="button"
      onClick={() => perguntar(s)}
      disabled={pensando}
      className="rounded-full border border-line bg-surface px-3 py-1.5 text-left text-[12px] text-ink hover:border-primary hover:bg-soft disabled:opacity-50"
    >
      {s}
    </button>
  );

  const porAssunto = (usuarioComum ? SUGESTOES_USUARIO : SUGESTOES).map((g) => (
    <div key={g.assunto}>
      <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">{g.assunto}</div>
      <div className="flex flex-wrap gap-1.5">{g.perguntas.map(sugestao)}</div>
    </div>
  ));

  const renderBloco = (b: Bloco, i: number) => {
    switch (b.tipo) {
      case 'titulo':
        return (
          <div key={i}>
            <div className="text-[13.5px] font-semibold text-ink">{b.titulo}</div>
            {b.subtitulo && <div className="text-[11.5px] text-muted-foreground">{b.subtitulo}</div>}
          </div>
        );
      case 'texto':
        return (
          <p key={i} className="whitespace-pre-wrap text-[12.5px] text-ink">
            {b.texto}
          </p>
        );
      case 'passos':
        return (
          <ol key={i} className="list-decimal space-y-1.5 pl-5 text-[12.5px] text-ink">
            {b.passos.map((p, j) => (
              <li key={j}>{p}</li>
            ))}
          </ol>
        );
      case 'link':
        return (
          <button key={i} type="button" onClick={() => ir(b.to)} className="self-start rounded-lg bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground">
            {b.rotulo}
          </button>
        );
      case 'metricas':
        return (
          <div key={i} className="grid grid-cols-2 gap-2">
            {b.itens.map((m, j) => (
              <div key={j} className={`rounded-lg border border-l-4 border-line bg-surface px-3 py-2 ${TOM[m.tom || 'neutro']}`}>
                <div className="text-[18px] font-semibold leading-tight text-ink">{num(m.valor)}</div>
                <div className="text-[11px] text-muted-foreground">{m.rotulo}</div>
              </div>
            ))}
          </div>
        );
      case 'barras':
        return <Barras key={i} titulo={b.titulo} itens={b.itens} />;
      case 'pizza':
        return <Pizza key={i} titulo={b.titulo} itens={b.itens} />;
      case 'linha':
        return <Linha key={i} titulo={b.titulo} pontos={b.pontos} />;
      case 'tabela':
        return (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[12px] font-semibold text-ink">{b.titulo}</div>
              <CopiarTabela colunas={b.colunas} linhas={b.linhas} />
            </div>
            <div className="max-h-64 overflow-auto rounded-lg border border-line">
              <table className="w-full text-left text-[11.5px]">
                <thead className="sticky top-0 bg-soft text-muted-foreground">
                  <tr>
                    {b.colunas.map((c, j) => (
                      <th key={j} className="whitespace-nowrap px-2 py-1.5 font-medium">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.linhas.map((l, j) => {
                    const to = b.links?.[j];
                    return (
                      <tr key={j} onClick={to ? () => ir(to) : undefined} className={`border-t border-line ${to ? 'cursor-pointer hover:bg-soft' : ''}`}>
                        {l.map((c, k) => (
                          // Nome com largura minima; o resto numa linha (a tabela rola de lado).
                          <td key={k} className={`px-2 py-1.5 text-ink ${k === 0 ? 'min-w-[150px]' : 'whitespace-nowrap'}`}>
                            {num(c)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  /* Tela cheia sumia (23/10/2026): sm:inset-auto ficava na classe fixa e
     competia com sm:inset-4 - no CSS do Tailwind o auto vencia e o painel ficava
     sem posicao nem tamanho. Agora o inset-auto so existe no modo normal. */
  const painel = cheio
    ? 'sm:inset-4'
    : 'sm:inset-auto sm:bottom-4 sm:right-4 sm:h-[600px] sm:max-h-[calc(100vh-2rem)] sm:w-[400px]';
  const iconeBtn = 'grid h-8 w-8 place-items-center rounded-md text-primary-foreground/85 hover:bg-white/15 hover:text-primary-foreground';
  const svgBtn = { viewBox: '0 0 24 24', className: 'h-4 w-4', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

  return (
    <>
      {!aberto && (
        <div className={`fixed right-4 z-[1350] flex items-center gap-2 ${naTickets ? 'bottom-[68px]' : 'bottom-4'}`}>
          {chamando && dica >= 0 && (
            <button
              type="button"
              onClick={() => setAberto(true)}
              className="max-w-[230px] rounded-xl border border-line bg-surface px-3 py-2 text-left text-[12px] text-ink shadow-card motion-safe:animate-in motion-safe:fade-in"
            >
              {DICAS[dica]}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAberto(true)}
            title="Assistente"
            aria-label="Abrir o assistente"
            className={`relative grid h-11 w-11 place-items-center rounded-full bg-primary text-primary-foreground shadow-card transition-transform hover:scale-105 ${
              chamando ? 'motion-safe:animate-chamar' : ''
            }`}
          >
            {chamando && <span aria-hidden className="absolute inset-0 rounded-full bg-primary/40 motion-safe:animate-ping" />}
            <IconeRobo className="relative h-6 w-6" />
          </button>
        </div>
      )}

      {aberto && (
        <div
          role="dialog"
          aria-label="Assistente"
          className={`fixed inset-0 z-[1400] flex flex-col overflow-hidden border-line bg-surface shadow-elevado sm:rounded-xl sm:border ${painel}`}
        >
          {/* Cabecalho na cor primaria com texto claro (como o modelo pedido): em
              fundo branco o subtitulo e os botoes ficavam quase invisiveis. */}
          <div className="flex items-center gap-3 bg-primary px-4 py-3 text-primary-foreground">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-primary-foreground">
              <IconeRobo className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold">Assistente</div>
              <div className="flex items-center gap-1.5 text-[11.5px] opacity-90">
                <span className="h-1.5 w-1.5 rounded-full bg-ok" />
                Online · só consulta
              </div>
            </div>
            <button type="button" title="O que posso perguntar?" aria-label="O que posso perguntar?" onClick={() => perguntar('ajuda')} disabled={pensando} className={iconeBtn}>
              <svg {...svgBtn}>
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5v.4M12 17h.01" />
              </svg>
            </button>
            <button type="button" title="Limpar conversa" aria-label="Limpar conversa" onClick={() => setMsgs([])} className={iconeBtn}>
              <svg {...svgBtn}>
                <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
              </svg>
            </button>
            <button type="button" title={cheio ? 'Reduzir' : 'Tela cheia'} aria-label="Expandir ou reduzir" onClick={() => setCheio((c) => !c)} className={`${iconeBtn} hidden sm:grid`}>
              <svg {...svgBtn}>
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            </button>
            <button type="button" title="Minimizar" aria-label="Minimizar" onClick={() => setAberto(false)} className={iconeBtn}>
              <svg {...svgBtn}>
                <path d="M5 12h14" />
              </svg>
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-surface-2 px-4 py-3">
            <div className="rounded-xl border border-line bg-surface p-3">
              <div className="text-[13.5px] font-semibold text-ink">Olá! Como posso ajudar?</div>
              <div className="mb-1 text-[11.5px] font-medium text-info">Só os dados que você já pode ver</div>
              <p className="text-[12.5px] text-muted-foreground">Eu consulto aparelhos, grupos e seus tickets, e explico como usar as telas. Não altero nada.</p>
              {msgs.length === 0 && (
                <div className="mt-3 space-y-2.5">
                  {porAssunto}
                  <p className="text-[11.5px] text-muted-foreground">{usuarioComum ? 'Ou digite só o nome de um grupo ou aparelho.' : 'Ou digite só o nome de um grupo, aparelho ou regional.'}</p>
                </div>
              )}
            </div>

            {msgs.map((m, i) =>
              m.papel === 'eu' ? (
                <div key={i} className="ml-auto max-w-[85%] whitespace-pre-wrap rounded-xl bg-primary px-3 py-2 text-[12.5px] text-primary-foreground">
                  {m.texto}
                  {m.hora && <div className="mt-0.5 text-right text-[10px] opacity-70">{m.hora}</div>}
                </div>
              ) : (
                <div key={i} className={`flex flex-col gap-2.5 rounded-xl border bg-surface p-3 ${m.erro ? 'border-danger' : 'border-line'}`}>
                  {m.texto && <p className={`text-[12.5px] ${m.erro ? 'text-danger' : 'text-ink'}`}>{m.texto}</p>}
                  {m.blocos?.map(renderBloco)}
                  {m.ajuda && <div className="space-y-2.5">{porAssunto}</div>}
                  {!!m.sugestoes?.length && <div className="flex flex-wrap gap-2">{m.sugestoes.map(sugestao)}</div>}
                  {m.hora && <div className="text-[10px] text-faint">{m.hora}</div>}
                </div>
              )
            )}
            {pensando && (
              <div className="flex w-fit items-center gap-1 rounded-xl border border-line bg-surface px-3 py-2" role="status" aria-label="digitando">
                {[0, 150, 300].map((d) => (
                  <span key={d} className="h-1.5 w-1.5 rounded-full bg-muted motion-safe:animate-bounce" style={{ animationDelay: `${d}ms` }} />
                ))}
                <span className="ml-1 text-[11.5px] text-muted-foreground">digitando...</span>
              </div>
            )}
            <div ref={fim} />
          </div>

          <div className="border-t border-line p-3">
            <div className="flex items-end gap-2">
              <textarea
                rows={1}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    perguntar(texto);
                  } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && (texto === '' || hist.current >= 0)) {
                    // Historico: so com a caixa vazia (ou ja navegando nele).
                    const lista = msgs.filter((m) => m.papel === 'eu' && m.texto).map((m) => m.texto as string);
                    if (!lista.length) return;
                    e.preventDefault();
                    const i = e.key === 'ArrowUp' ? (hist.current < 0 ? lista.length - 1 : Math.max(0, hist.current - 1)) : hist.current + 1;
                    if (i >= lista.length) {
                      hist.current = -1;
                      setTexto('');
                    } else {
                      hist.current = i;
                      setTexto(lista[i]);
                    }
                  }
                }}
                placeholder={usuarioComum ? 'Pergunte ou digite o nome de um grupo ou aparelho...' : 'Pergunte ou digite o nome de um grupo ou aparelho...'}
                className="max-h-28 min-h-[38px] flex-1 resize-none rounded-lg border border-line bg-surface px-3 py-2 text-[12.5px] text-ink outline-none placeholder:text-faint focus:border-primary"
              />
              <button
                type="button"
                onClick={() => perguntar(texto)}
                disabled={pensando || !texto.trim()}
                aria-label="Enviar"
                className="grid h-[38px] w-[38px] place-items-center rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
              >
                <svg {...svgBtn}>
                  <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
                </svg>
              </button>
            </div>
            <div className="mt-2 text-[11px] text-faint">Só consulta: o assistente não altera nada. Confira na tela antes de agir.</div>
          </div>
        </div>
      )}
    </>
  );
}
